/**
 * Multi-tenant client for PayPunch.
 *
 * Resolves the active tenant_id in this priority order:
 *   1) ?tenant=X in the current URL (and persists to localStorage)
 *   2) localStorage("paypunch_tenant")
 *   3) "oakton" (default — Market Place on Oakton)
 *
 * Then attaches an axios interceptor so every backend call includes
 * the X-Tenant-Id header automatically. No other code changes needed.
 */
import axios from "axios";

const STORAGE_KEY = "paypunch_tenant";
const EMBED_KEY = "paypunch_embed";
const DEFAULT_TENANT = "oakton";

function readFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const t = (p.get("tenant") || "").trim().toLowerCase();
    return t || null;
  } catch {
    return null;
  }
}

function readFromStorage() {
  try {
    return (localStorage.getItem(STORAGE_KEY) || "").trim().toLowerCase() || null;
  } catch {
    return null;
  }
}

function readEmbedFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const val = (p.get("embed") || "").trim();
    return val === "1" || val === "true";
  } catch {
    return false;
  }
}

/**
 * True when PayPunch is being rendered inside a parent app's iframe.
 * We detect it in three ways:
 *   1) ?embed=1 in the URL (preferred — set by the parent app)
 *   2) window !== window.top (iframe context)
 *   3) Sticky localStorage flag set by a previous embed load
 */
export function isEmbedded() {
  if (readEmbedFromUrl()) {
    try { sessionStorage.setItem(EMBED_KEY, "1"); } catch {}
    return true;
  }
  try {
    if (sessionStorage.getItem(EMBED_KEY) === "1") return true;
  } catch {}
  try {
    if (window.self !== window.top) return true;
  } catch {
    // Cross-origin iframe throws — that itself confirms we're embedded
    return true;
  }
  return false;
}

export function resolveTenant() {
  const urlTenant = readFromUrl();
  if (urlTenant) {
    try {
      localStorage.setItem(STORAGE_KEY, urlTenant);
    } catch {}
    return urlTenant;
  }
  const stored = readFromStorage();
  return stored || DEFAULT_TENANT;
}

export function getTenantId() {
  return resolveTenant();
}

export function setTenantId(tid) {
  try {
    localStorage.setItem(STORAGE_KEY, (tid || DEFAULT_TENANT).toLowerCase());
  } catch {}
}

/**
 * Installs a one-time axios request interceptor that attaches
 * X-Tenant-Id to every outgoing request. Idempotent.
 */
let _installed = false;
export function installTenantInterceptor() {
  if (_installed) return;
  _installed = true;
  axios.interceptors.request.use((config) => {
    const tid = getTenantId();
    config.headers = config.headers || {};
    if (!config.headers["X-Tenant-Id"]) {
      config.headers["X-Tenant-Id"] = tid;
    }
    return config;
  });
}
